import { useRecordContext, useTranslate } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { RecordField } from "@/components/admin/record-field";
import { ShowButton } from "@/components/admin/show-button";

import { AsideSection } from "../misc/AsideSection";
import { formatDate } from "../misc/formatDate";
import type { Company } from "../types";
import { CompanyDeleteButton } from "./CompanyDeleteButton";

interface CompanyAsideProps {
  /** Which button links to the other page: "edit" on the show page, "show" on the edit page. */
  link?: "edit" | "show";
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  if (!record) return null;

  return (
    <div className="hidden sm:block w-80 min-w-80 space-y-4">
      <div className="flex flex-row flex-wrap gap-2">
        {link === "edit" ? (
          <EditButton label="ra.action.edit" />
        ) : (
          <ShowButton label="ra.action.show" />
        )}
        <CompanyDeleteButton label="ra.action.delete" />
      </div>

      <CompanyInfo record={record} />
      <CompanyRemarks record={record} />
    </div>
  );
};

/** Address and invoice e-mail; also used on the mobile show page. */
export const CompanyInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const empty = translate("resources.companies.not_filled");

  return (
    <AsideSection title={translate("crm.common.details")}>
      <RecordField
        record={record}
        resource="companies"
        source="address"
        empty={empty}
      />
      <RecordField
        record={record}
        resource="companies"
        source="invoice_email"
        empty={empty}
        className="[&_span]:break-all"
      />
    </AsideSection>
  );
};

/** The free-text "Opmerkingen" plus the creation date. */
export const CompanyRemarks = ({ record }: { record: Company }) => {
  const translate = useTranslate();

  return (
    <AsideSection title={translate("resources.companies.fields.description")}>
      {record.description ? (
        <p className="text-sm whitespace-pre-line">{record.description}</p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("resources.companies.not_filled")}
        </p>
      )}
      {record.created_at ? (
        <p className="text-xs text-muted-foreground pt-2">
          {translate("resources.companies.added_on", {
            date: formatDate(record.created_at),
          })}
        </p>
      ) : null}
    </AsideSection>
  );
};
